import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import { locationsApi, QueryLocationParams, CreateLocationDto } from '../services/locations.api';
import { toast } from 'sonner';
import { getErrorMessage } from '../client';

/**
 * Query hook for fetching paginated locations list
 */
export function useLocationsList(params: QueryLocationParams = {}) {
  return useQuery({
    queryKey: queryKeys.locations.list(params),
    queryFn: () => locationsApi.getAll(params),
  });
}

/**
 * Query hook for fetching a single location
 */
export function useLocation(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.locations.detail(id || ''),
    queryFn: () => locationsApi.getById(id!),
    enabled: !!id,
  });
}

/**
 * Query hook for fetching location inventory
 */
export function useLocationInventory(
  id: string | undefined,
  params?: { page?: number; limit?: number }
) {
  return useQuery({
    queryKey: [...queryKeys.locations.detail(id || ''), 'inventory', params],
    queryFn: () => locationsApi.getInventory(id!, params),
    enabled: !!id,
  });
}

/**
 * Mutation hook for creating a location
 */
export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLocationDto) => locationsApi.create(data),
    onSuccess: () => {
      toast.success('Location created successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for updating a location
 */
export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateLocationDto> }) =>
      locationsApi.update(id, data),
    onSuccess: (_, variables) => {
      toast.success('Location updated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.detail(variables.id) });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for deleting a location
 */
export function useDeleteLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => locationsApi.delete(id),
    onSuccess: () => {
      toast.success('Location deleted successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
